import { ApiModule } from './../api/api.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { StoreModule } from '@ngrx/store';
import {
  globalPayInitialState,
  globalPayStateReducer,
  nbgPayInitialState,
  nbgPayStateReducer,
  realexPaymentStateReducer,
  realexPaymentInitialState,
  rmsInitialState,
  rmsStateReducer,
  integrationPaymentStateReducer,
  integrationPaymentInitialState,
} from '../public/payments/+state/payment.reducer';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { PagerComponent } from './pager/pager.component';
import { DownloadDirective } from './download.directive';
import { EnumPipe } from './enum.pipe';
import { HighlightSearchPipe } from './highlight-search/highlights-search.pipe';
import { StoreImageUploadComponent } from './store-image-upload/store-image-upload.component';
import { TranslateModule } from '@ngx-translate/core';
import { DisableControlDirective } from './disable-control.directive';
import { LocalizedDatePipe } from './localized-date.pipe';
import { LocalizedCurrencyPipe } from './localized-currency.pipe';
import { FormatPrice } from './format-price.pipe';
import { HelpIconComponent } from './help-icon/help-icon.component';
import { ExplanationComponent } from './explanation/explanation.component';
import { InfoTooltipComponent } from './info-tooltip/info-tooltip.component';
import { PriceInputComponent } from './price-input/price-input.component';
import { PreventDoubleClickDirective } from './directives/prevent-double-click.directive';
import { NotificationSoundDirective } from './directives/notification-sound.directive';
import { NgxTrimDirective, NgxTrimDirectiveModule } from 'ngx-trim-directive';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MonthDatePickerComponent } from './month-date-picker/month-date-picker.component';
import { DayDatePickerComponent } from './day-date-picker/day-date-picker.component';
import { DatePresetSelectorComponent } from './date-preset-selector/date-preset-selector.component';
import { GoTimepickerComponent } from './go-timepicker/go-timepicker.component';
import { AuthEffects } from '../auth/+state/auth.effects';
import {
  AUTH_LOCAL_STORAGE_KEY,
  AUTH_STORAGE_KEYS,
  AUTH_CONFIG_TOKEN,
} from '../auth/auth.tokens';
import { LocalStorageService } from '../local-storage.service';
import { storageMetaReducer } from '../storage.metareducer';
import { UserLoginComponent } from './user-login/user-login.component';
import { GoogleSigninButtonModule } from '@abacritt/angularx-social-login';
import { CommonFormModule } from './common-form/common-form.module';
import { PhoneInputComponent } from './phone-input/phone-input.component';
import { LoadingPanelComponent } from './loading-panel/loading-panel.component';
import { PreventEnterDirective } from './directives/prevent-enter.directive';
import { CustomGoogleDirective } from './custom-google.directive';
import { GoToast } from './go-toast.component';
import { InviteUserDialogComponent } from './invite-user-dialog/invite-user-dialog.component';
import { CustomDatepickerComponent } from '../stores/custom-datepicker/custom-datepicker.component';
import { AllergensComponent } from './allergens/allergens.component';
import { TopCatalogViewComponent } from './top-catalog-view/top-catalog-view.component';
import { DefaultCategoryTilesComponent } from './default-category-tiles/default-category-tiles.component';
import { OfferPriceComponent } from './offer-price/offer-price.component';
import { OfferActionsComponent } from './offer-actions/offer-actions.component';
import { ViewOrderButtonComponent } from './view-order-button/view-order-button.component';
import { SafePipe } from './safe.pipe';
import { ApiTokenGenerateComponent } from './api-token-generate/api-token-generate.component';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { InputTextComponent } from './input-text/input-text.component';
import { FormattedTimePipe } from './formatted-time.pipe';
import { StoreDatePipe } from './store-date.pipe';
import { StoreTimezoneService } from './store-timezone.service';
import { TermsPrivacyComponent } from './terms-privacy/terms-privacy.component';
import { TermsAcceptanceComponent } from './terms-acceptance/terms-acceptance.component';
import {
  AsideToggleDirective,
  HtmlAttributesDirective,
  SidebarToggleDirective,
} from './coreui-fixture/shared/layout/layout.directive';
import { CollapsePanelComponent } from './collapse-panel/collapse-panel.component';
import { ButtonComponent } from './button/button.component';
import { FormFooterComponent } from './form-footer/form-footer.component';
import { AddressFieldsComponent } from './address-fields/address-fields.component';
import { AvatarComponent } from './avatar/avatar.component';
import { DialogComponent } from './dialog/dialog.component';
import { DialogActionsComponent } from './dialog/dialog-actions/dialog-actions.component';
import { DialogContentComponent } from './dialog/dialog-content/dialog-content.component';
import { AvailableSlotsDialogComponent } from './dialog/available-slots-dialog/available-slots-dialog.component';
import { AvailableSlotsDialogContentComponent } from './dialog/available-slots-dialog/available-slots-dialog-content.component';
import { AvailableSlotsDialogActionsComponent } from './dialog/available-slots-dialog/available-slots-dialog-actions.component';
import { MatDialogModule } from '@angular/material/dialog';
import { ConfirmDialogComponent } from './dialog/confirm-dialog/confirm-dialog.component';
import { AlertDialogComponent } from './dialog/alert-dialog/alert-dialog.component';
import { ImageUploadComponent } from './image-upload/image-upload.component';
import { UploadVideoComponent } from './upload-video/upload-video.component';
import { HorizontalCategoryNavComponent } from './horizontal-category-nav/horizontal-category-nav.component';
import { TagsInputComponent } from './tags-input/tags-input.component';
import { MapPinWidgetComponent } from './map-pin-widget/map-pin-widget.component';
import { SecureUrlInputComponent } from './secure-url-input/secure-url-input.component';
import { FormInputFieldComponent } from './form-input-field/form-input-field.component';
import { TermsPrivacyPlatformComponent } from './terms-privacy-platform/terms-privacy-platform.component';
import { ConnectButtonComponent } from './connect-button/connect-button.component';
import { DisconnectButtonComponent } from './disconnect-button/disconnect-button.component';
import { WishtimeGroupHeaderComponent } from './wishtime-group-header/wishtime-group-header.component';
import { ManageItemsModalComponent } from './manage-items-modal/manage-items-modal.component';
import { ItemImageComponent } from './item-image/item-image.component';
import { PaymentFailedContentComponent } from './payment-failed-content/payment-failed-content.component';
import { VoucherSelectionDialogComponent } from './voucher-selection-dialog/voucher-selection-dialog.component';
import { CardHeaderComponent } from './card-header/card-header.component';
import { NavigationTabsComponent } from './navigation-tabs/navigation-tabs.component';
import { VoucherListComponent } from './voucher-list/voucher-list.component';
import { RichTextPipe } from '../public/rich-text.pipe';
import { OfferListCardComponent } from './offer-list-card/offer-list-card.component';
import { CheckboxComponent } from './checkbox/checkbox.component';
import { RadioGroupComponent } from './radio-group/radio-group.component';
import { ToggleComponent } from './toggle/toggle.component';
import { SelectComponent } from './select/select.component';
import { SelectWithActionComponent } from './select-with-action/select-with-action.component';
import { FormRowComponent } from './form-row/form-row.component';
import { DetailRowComponent } from './detail-row/detail-row.component';
import { OrderMetaRowComponent } from './order-meta-row/order-meta-row.component';
import { SearchableMultiSelectComponent } from './searchable-multi-select/searchable-multi-select.component';
import { ItemImageUploadComponent } from './item-image-upload/item-image-upload.component';
import { DatePickerComponent } from './date-picker/date-picker.component';
import { DataTableComponent } from './data-table/data-table.component';
import { DataTableCellDirective } from './data-table/data-table-cell.directive';
import { FileInputComponent } from './file-input/file-input.component';
import { ColorSwatchComponent } from './color-swatch/color-swatch.component';
import { RangeSliderComponent } from './range-slider/range-slider.component';
import { BrandColorPickerComponent } from './brand-color-picker/brand-color-picker.component';
import { ColorPickerModule } from 'ngx-color-picker';
import { IconComponent } from './icon/icon.component';
import { MatInputComponent } from './mat-input/mat-input.component';
import { HiddenInputComponent } from './hidden-input/hidden-input.component';
import { AutocompleteInputComponent } from './autocomplete-input/autocomplete-input.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CheckoutPaymentFormsComponent } from '../public/store/checkout-payment/checkout-payment-forms/checkout-payment-forms.component';
import { ApcopayComponent } from '../public/payments/apcopay/apcopay.component';
import { JccComponent } from '../public/payments/jcc/jcc.component';
import { TrustpaymentComponent } from '../public/payments/trustpayment/trustpayment.component';
import { CardlinkComponent } from '../public/payments/cardlink/cardlink.component';
import { EurobankComponent } from '../public/payments/eurobank/eurobank.component';
import { AlphabankComponent } from '../public/payments/alphabank/alphabank.component';
import { GlobalpayComponent } from '../public/payments/globalpay/globalpay.component';
import { NbgpayComponent } from '../public/payments/nbgpay/nbgpay.component';
import { RmsComponent } from '../public/payments/rms/rms.component';
import { RealexpaymentComponent } from '../public/payments/realexpayment/realexpayment.component';
import { HobexComponent } from '../public/payments/hobex/hobex.component';
import { RyftComponent } from '../public/payments/ryft/ryft.component';
import { ClearacceptComponent } from '../public/payments/clearaccept/clearaccept.component';
import { StorePaymentPayComponent } from '../public/store/store-payment-pay/store-payment-pay.component';
import { StoreCatalogLanguageSelectorComponent } from '../public/store/store-catalog-language-selector/store-catalog-language-selector.component';

export function getAuthConfig(
  saveKeys: string,
  localStorageKey: string,
  storageService: LocalStorageService,
) {
  return {
    metaReducers: [
      storageMetaReducer(
        ({ tokens }) => ({ tokens }),
        localStorageKey,
        storageService,
      ),
    ],
  };
}

@NgModule({
  declarations: [
    PagerComponent,
    DownloadDirective,
    EnumPipe,
    HighlightSearchPipe,
    StoreImageUploadComponent,
    DisableControlDirective,
    LocalizedDatePipe,
    LocalizedCurrencyPipe,
    HelpIconComponent,
    ExplanationComponent,
    InfoTooltipComponent,
    FormatPrice,
    PriceInputComponent,
    PreventDoubleClickDirective,
    NotificationSoundDirective,
    MonthDatePickerComponent,
    DayDatePickerComponent,
    DatePresetSelectorComponent,
    GoTimepickerComponent,
    UserLoginComponent,
    PhoneInputComponent,
    LoadingPanelComponent,
    PreventEnterDirective,
    CustomGoogleDirective,
    InviteUserDialogComponent,
    ImageUploadComponent,
    GoToast,
    CustomDatepickerComponent,
    AllergensComponent,
    TopCatalogViewComponent,
    DefaultCategoryTilesComponent,
    OfferPriceComponent,
    OfferActionsComponent,
    ViewOrderButtonComponent,
    SafePipe,
    ApiTokenGenerateComponent,
    InputTextComponent,
    FormattedTimePipe,
    StoreDatePipe,
    TermsPrivacyComponent,
    TermsAcceptanceComponent,
    CollapsePanelComponent,
    ButtonComponent,
    FormFooterComponent,
    AddressFieldsComponent,
    AvatarComponent,
    DialogComponent,
    DialogActionsComponent,
    DialogContentComponent,
    AvailableSlotsDialogComponent,
    AvailableSlotsDialogContentComponent,
    AvailableSlotsDialogActionsComponent,
    ConfirmDialogComponent,
    AlertDialogComponent,
    UploadVideoComponent,
    HorizontalCategoryNavComponent,
    TagsInputComponent,
    MapPinWidgetComponent,
    SecureUrlInputComponent,
    TermsPrivacyPlatformComponent,
    FormInputFieldComponent,
    ConnectButtonComponent,
    DisconnectButtonComponent,
    CardHeaderComponent,
    WishtimeGroupHeaderComponent,
    ManageItemsModalComponent,
    ItemImageComponent,
    PaymentFailedContentComponent,
    VoucherSelectionDialogComponent,
    NavigationTabsComponent,
    VoucherListComponent,
    RichTextPipe,
    OfferListCardComponent,
    CheckboxComponent,
    RadioGroupComponent,
    ToggleComponent,
    SelectComponent,
    SelectWithActionComponent,
    FormRowComponent,
    DetailRowComponent,
    OrderMetaRowComponent,
    SearchableMultiSelectComponent,
    ItemImageUploadComponent,
    FileInputComponent,
    MatInputComponent,
    HiddenInputComponent,
    AutocompleteInputComponent,
    ColorSwatchComponent,
    RangeSliderComponent,
    BrandColorPickerComponent,
    DatePickerComponent,
    DataTableComponent,
    DataTableCellDirective,
    CheckoutPaymentFormsComponent,
    ApcopayComponent,
    JccComponent,
    TrustpaymentComponent,
    CardlinkComponent,
    EurobankComponent,
    AlphabankComponent,
    GlobalpayComponent,
    NbgpayComponent,
    RmsComponent,
    RealexpaymentComponent,
    HobexComponent,
    RyftComponent,
    ClearacceptComponent,
    StorePaymentPayComponent,
    StoreCatalogLanguageSelectorComponent,
  ],
  exports: [
    IconComponent,
    PagerComponent,
    DownloadDirective,
    EnumPipe,
    HighlightSearchPipe,
    StoreImageUploadComponent,
    TranslateModule,
    DisableControlDirective,
    LocalizedDatePipe,
    LocalizedCurrencyPipe,
    HelpIconComponent,
    ExplanationComponent,
    InfoTooltipComponent,
    FormatPrice,
    PriceInputComponent,
    PreventDoubleClickDirective,
    NotificationSoundDirective,
    NgxTrimDirective,
    MonthDatePickerComponent,
    DayDatePickerComponent,
    DatePresetSelectorComponent,
    GoTimepickerComponent,
    UserLoginComponent,
    CommonFormModule,
    PhoneInputComponent,
    LoadingPanelComponent,
    PreventEnterDirective,
    CustomGoogleDirective,
    GoToast,
    CustomDatepickerComponent,
    AllergensComponent,
    TopCatalogViewComponent,
    DefaultCategoryTilesComponent,
    OfferPriceComponent,
    OfferActionsComponent,
    ViewOrderButtonComponent,
    SafePipe,
    InputTextComponent,
    FormattedTimePipe,
    StoreDatePipe,
    TermsPrivacyComponent,
    TermsAcceptanceComponent,
    SidebarToggleDirective,
    AsideToggleDirective,
    HtmlAttributesDirective,
    CollapsePanelComponent,
    ButtonComponent,
    FormFooterComponent,
    AddressFieldsComponent,
    AvatarComponent,
    DialogComponent,
    DialogActionsComponent,
    DialogContentComponent,
    AvailableSlotsDialogComponent,
    AvailableSlotsDialogContentComponent,
    AvailableSlotsDialogActionsComponent,
    ConfirmDialogComponent,
    AlertDialogComponent,
    ImageUploadComponent,
    MatSelectModule,
    UploadVideoComponent,
    HorizontalCategoryNavComponent,
    TagsInputComponent,
    MapPinWidgetComponent,
    SecureUrlInputComponent,
    TermsPrivacyPlatformComponent,
    FormInputFieldComponent,
    ConnectButtonComponent,
    DisconnectButtonComponent,
    CardHeaderComponent,
    WishtimeGroupHeaderComponent,
    ManageItemsModalComponent,
    ItemImageComponent,
    PaymentFailedContentComponent,
    VoucherSelectionDialogComponent,
    NavigationTabsComponent,
    VoucherListComponent,
    RichTextPipe,
    OfferListCardComponent,
    CheckboxComponent,
    RadioGroupComponent,
    ToggleComponent,
    SelectComponent,
    SelectWithActionComponent,
    FormRowComponent,
    DetailRowComponent,
    OrderMetaRowComponent,
    SearchableMultiSelectComponent,
    ItemImageUploadComponent,
    FileInputComponent,
    MatInputComponent,
    HiddenInputComponent,
    AutocompleteInputComponent,
    ColorSwatchComponent,
    RangeSliderComponent,
    BrandColorPickerComponent,
    DatePickerComponent,
    DataTableComponent,
    DataTableCellDirective,
    CheckoutPaymentFormsComponent,
    ApcopayComponent,
    JccComponent,
    TrustpaymentComponent,
    CardlinkComponent,
    EurobankComponent,
    AlphabankComponent,
    GlobalpayComponent,
    NbgpayComponent,
    RmsComponent,
    RealexpaymentComponent,
    HobexComponent,
    RyftComponent,
    ClearacceptComponent,
    StorePaymentPayComponent,
    StoreCatalogLanguageSelectorComponent,
  ],
  imports: [
    IconComponent,
    ColorPickerModule,
    MatAutocompleteModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatSelectModule,
    PopoverModule,
    ApiModule,
    TranslateModule,
    NgxTrimDirectiveModule,
    GoogleSigninButtonModule,
    CommonFormModule,
    RouterModule,
    SidebarToggleDirective,
    AsideToggleDirective,
    HtmlAttributesDirective,
    StoreModule.forFeature(
      'integrationPaymentState',
      integrationPaymentStateReducer,
      { initialState: integrationPaymentInitialState },
    ),
    StoreModule.forFeature('globalPayState', globalPayStateReducer, {
      initialState: globalPayInitialState,
    }),
    StoreModule.forFeature('nbgPayState', nbgPayStateReducer, {
      initialState: nbgPayInitialState,
    }),
    StoreModule.forFeature('rmsState', rmsStateReducer, {
      initialState: rmsInitialState,
    }),
    StoreModule.forFeature('realexPaymentState', realexPaymentStateReducer, {
      initialState: realexPaymentInitialState,
    }),
  ],
  providers: [
    FormatPrice,
    AuthEffects,
    StoreTimezoneService,
    { provide: AUTH_LOCAL_STORAGE_KEY, useValue: '__auth_storage__' },
    { provide: AUTH_STORAGE_KEYS, useValue: 'auth' },
    {
      provide: AUTH_CONFIG_TOKEN,
      deps: [AUTH_STORAGE_KEYS, AUTH_LOCAL_STORAGE_KEY, LocalStorageService],
      useFactory: getAuthConfig,
    },
    DatePipe,
    provideHttpClient(withInterceptorsFromDi()),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedModule {}
